import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Sprout, ArrowRight, Trash2, CheckCircle2 } from "lucide-react";

const INFESTATIONS = ["Torpedo Grass", "Crabgrass", "Both"];

export const TurfManager = ({ clients = [] }) => {
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({ client_id: "", address: "", infestation_type: [], square_feet: "", sod_type: "St. Augustine" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/turf").then((r) => setProjects(r.data));
  useEffect(() => { load(); }, []);

  const toggleInf = (v) => setForm((f) => ({
    ...f, infestation_type: f.infestation_type.includes(v) ? f.infestation_type.filter((x) => x !== v) : [...f.infestation_type, v],
  }));

  const create = async (e) => {
    e.preventDefault();
    if (!form.client_id || !form.address || form.infestation_type.length === 0 || !form.square_feet) {
      toast.error("Pick a client, address, infestation and square footage"); return;
    }
    setBusy(true);
    try {
      await api.post("/turf", { ...form, square_feet: Number(form.square_feet) });
      toast.success("Turf project created · awaiting client waiver");
      setForm({ client_id: "", address: "", infestation_type: [], square_feet: "", sod_type: "St. Augustine" });
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const advance = async (p) => {
    try { await api.post(`/turf/${p.id}/advance`); toast.success("Phase advanced"); await load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const remove = async (p) => {
    try { await api.delete(`/turf/${p.id}`); toast.success("Project removed"); await load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="turf-manager" className="rounded-2xl border border-black/5 bg-white p-6 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
      <h2 className="font-serif text-2xl text-emerald-950 flex items-center gap-2"><Sprout className="w-5 h-5 text-emerald-700" /> Turf Renovation projects</h2>
      <p className="mt-1 text-xs text-stone-400">Multi-phase Torpedo/Crabgrass eradication → sod. Client must sign the biological waiver before work begins.</p>

      <form onSubmit={create} className="mt-4 grid sm:grid-cols-2 gap-2">
        <select data-testid="turf-client-select" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}
          className="rounded-xl border border-stone-200 h-10 px-3 text-sm">
          <option value="">Select client…</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.email})</option>)}
        </select>
        <Input data-testid="turf-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Property address" className="rounded-xl" />
        <Input data-testid="turf-sqft" type="number" value={form.square_feet} onChange={(e) => setForm({ ...form, square_feet: e.target.value })} placeholder="Square feet" className="rounded-xl" />
        <Input data-testid="turf-sod" value={form.sod_type} onChange={(e) => setForm({ ...form, sod_type: e.target.value })} placeholder="Sod type" className="rounded-xl" />
        <div className="sm:col-span-2 flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-[0.15em] text-stone-400">Infestation</span>
          {INFESTATIONS.map((v) => (
            <button key={v} type="button" data-testid={`turf-inf-${v}`} onClick={() => toggleInf(v)}
              className={`rounded-full px-3 py-1 text-xs ${form.infestation_type.includes(v) ? "bg-emerald-900 text-white" : "bg-stone-100 text-stone-600"}`}>{v}</button>
          ))}
        </div>
        <Button data-testid="turf-create-btn" type="submit" disabled={busy} className="sm:col-span-2 rounded-full bg-emerald-900 hover:bg-emerald-800 text-white">Create project</Button>
      </form>

      <div className="mt-5 space-y-3">
        {projects.length === 0 && <p className="text-sm text-stone-400">No turf projects yet.</p>}
        {projects.map((p) => {
          const done = p.current_phase === "Completed";
          return (
            <div key={p.id} data-testid={`turf-project-${p.id}`} className="rounded-xl border border-stone-100 bg-stone-50/60 p-4">
              <div className="flex items-start justify-between flex-wrap gap-2">
                <div>
                  <p className="font-semibold text-emerald-950">{p.client_name} · {p.address}</p>
                  <p className="text-xs text-stone-500">{p.infestation_type.join(", ")} · {p.square_feet} sq ft · {p.sod_type}</p>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <Badge data-testid={`turf-phase-${p.id}`} className={`rounded-full ${done ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{p.current_phase}</Badge>
                    {p.status === "draft"
                      ? <span data-testid={`turf-awaiting-${p.id}`} className="text-xs text-red-600">Awaiting client waiver</span>
                      : <span className="text-xs text-emerald-700">Waiver signed{p.waiver?.signature_name ? ` · ${p.waiver.signature_name}` : ""}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {p.status !== "draft" && !done && (
                    <Button data-testid={`turf-advance-${p.id}`} size="sm" onClick={() => advance(p)} className="rounded-full bg-emerald-900 hover:bg-emerald-800 text-white">
                      Advance <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                  {done && <CheckCircle2 className="w-5 h-5 text-emerald-600" />}
                  <Button data-testid={`turf-remove-${p.id}`} size="sm" variant="outline" onClick={() => remove(p)} className="rounded-full text-red-600 border-red-200 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
